import { appStateForModalsAtom } from '@/atoms/launchModalsConfigAtom';
import {
  PACING_MAX_PER_SESSION,
  isModalPacingEnabledAtom,
  pacedModalSeenCountAtom,
} from '@/atoms/modalPacing';
import {
  currentQueuedModalAtom,
  modalQueueAtom,
  pacedModalsAllowedAtom,
} from '@/atoms/modalQueueAtoms';
import { Button } from '@heidi/design-system/button';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';

const boolLabel = (value: boolean): string => (value ? 'yes' : 'no');

type PacingStatProps = {
  label: string;
  value: string;
  testId: string;
};

const PacingStat = ({ label, value, testId }: PacingStatProps) => (
  <div className="flex items-baseline justify-between gap-3">
    <TypographyCaption className="text-text-secondary">{label}</TypographyCaption>
    <TypographyCaption
      className="shrink-0 font-mono text-text-primary"
      data-testid={testId}
    >
      {value}
    </TypographyCaption>
  </div>
);

/**
 * Dev-tools UI for flag-gated modal pacing (`release-growth-modal-pacing`).
 * Same actions as `__heidiModals.pacing()`.
 */
export function ModalPacingDevSection() {
  const flagEnabled = useAtomValue(isModalPacingEnabledAtom);
  const appState = useAtomValue(appStateForModalsAtom);
  const dismissedCount = useAtomValue(pacedModalSeenCountAtom);
  const pacedAllowed = useAtomValue(pacedModalsAllowedAtom);
  const queueTop = useAtomValue(modalQueueAtom).at(0)?.id ?? '—';
  const current = useAtomValue(currentQueuedModalAtom)?.id ?? '—';

  const setDismissedCount = useSetAtom(pacedModalSeenCountAtom);

  const withholdingPacedModal = queueTop !== current;

  const handleReset = useCallback(() => {
    setDismissedCount(0);
  }, [setDismissedCount]);

  const handleForceCap = useCallback(() => {
    setDismissedCount(PACING_MAX_PER_SESSION);
  }, [setDismissedCount]);

  return (
    <section aria-labelledby="dev-tools-modal-pacing-heading" className="mb-6">
      <TypographyP2SemiBold id="dev-tools-modal-pacing-heading" className="mb-2">
        Modal pacing
      </TypographyP2SemiBold>
      <TypographyCaption className="mb-3 text-text-tertiary">
        Throttles non-essential launch surfaces when{' '}
        <span className="font-mono">release-growth-modal-pacing</span> is on. Cap is per
        tab / session. Values refresh on interaction.
      </TypographyCaption>

      <div className="mb-3 flex flex-col gap-1.5 rounded-md border border-border p-3">
        <PacingStat
          label="Flag enabled"
          value={boolLabel(flagEnabled)}
          testId="dev-tools-modal-pacing-flag-enabled"
        />
        <PacingStat
          label="Past quiet period (≥7d)"
          value={boolLabel(appState.isUserAtLeastAWeekOld)}
          testId="dev-tools-modal-pacing-quiet-period"
        />
        <PacingStat
          label="Recording active"
          value={boolLabel(appState.isRecording)}
          testId="dev-tools-modal-pacing-recording"
        />
        <PacingStat
          label="Dismissed this session"
          value={`${dismissedCount} / ${PACING_MAX_PER_SESSION}`}
          testId="dev-tools-modal-pacing-dismissed-count"
        />
        <PacingStat
          label="Paced surfaces allowed"
          value={boolLabel(pacedAllowed)}
          testId="dev-tools-modal-pacing-allowed"
        />
        <PacingStat
          label="Queue top"
          value={queueTop}
          testId="dev-tools-modal-pacing-queue-top"
        />
        <PacingStat
          label="Currently showing"
          value={current}
          testId="dev-tools-modal-pacing-current"
        />
        <PacingStat
          label="Withholding paced modal"
          value={boolLabel(withholdingPacedModal)}
          testId="dev-tools-modal-pacing-withholding"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleReset}
          data-testid="dev-tools-modal-pacing-reset"
        >
          Reset pacing
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleForceCap}
          data-testid="dev-tools-modal-pacing-force-cap"
        >
          Force cap ({PACING_MAX_PER_SESSION})
        </Button>
      </div>
    </section>
  );
}
